import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';
import TypeSearch from '../../components/common/TypeSearch';
import { postJournal, voidJournal } from '../../api/journal';
import { ACCOUNTS, rateFor, payableAccountForSupplierType, cogsAccountForSupplierType, getSupplierDepositRemaining } from '../../api/accounts';

export default function BookingForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAgent, agentRefId, branchId } = useAuth();
  const username = user?.email?.split('@')[0] || 'unknown';
  const isView = !!id;

  const [booking, setBooking] = useState(null);
  const [clientLabel, setClientLabel] = useState('');
  const [packages, setPackages] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [packageRates, setPackageRates] = useState([]);
  const [agents, setAgents] = useState([]);
  const [form, setForm] = useState({
    client_id: searchParams.get('client') || '',
    package_id: '',
    care_of_agent_id: '',
    care_of_agent_label: '',
    num_pax: '1',
    booking_status: 'PENDING',
    payment_status: 'UNPAID',
    sell_per_pax: '',
    sell_currency_id: '',
    exchange_rate: '1',
    travel_date: '',
    return_date: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [cancelModal, setCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [bookingSuppliers, setBookingSuppliers] = useState([]);
  const [supplierRefEdit, setSupplierRefEdit] = useState({});
  const [savingRef, setSavingRef] = useState({});
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  useEffect(() => { if (branchId && !selectedBranchId) setSelectedBranchId(branchId); }, [branchId]);

  useEffect(() => {
    supabase.from('branches').select('id, name, code').eq('is_active', true).order('name').then(({ data }) => setBranches(data || []));
    supabase.from('packages').select('id, package_name, package_code').eq('is_active', true).order('package_name').then(({ data }) => setPackages(data || []));
    supabase.from('currencies').select('id, code').eq('is_active', true).order('code').then(({ data }) => setCurrencies(data || []));
    supabase.from('agents').select('id, tls_code, full_name').eq('is_active', true).order('tls_code').then(({ data }) => {
      const mapped = (data || []).map(a => ({ ...a, label: `${a.tls_code} — ${a.full_name}` }));
      setAgents(mapped);
      if (isAgent && agentRefId && !id) {
        const me = mapped.find(a => a.id === agentRefId);
        if (me) setForm(f => ({ ...f, care_of_agent_id: me.id, care_of_agent_label: me.label }));
      }
    });
    if (id) {
      supabase.from('bookings').select('*, clients(full_name), packages(package_name), sell_currency:sell_currency_id(code), care_of_agent:care_of_agent_id(tls_code, full_name)').eq('id', id).single().then(({ data }) => setBooking(data));
      supabase.from('booking_suppliers').select('*, suppliers(name, supplier_type)').eq('booking_id', id).order('created_at')
        .then(({ data }) => {
          setBookingSuppliers(data || []);
          const refs = {};
          (data || []).forEach(r => { refs[r.id] = r.supplier_ref || ''; });
          setSupplierRefEdit(refs);
        });
    }
  }, [id]);

  const [selectedPkg, setSelectedPkg] = useState(null);
  const [commTiers, setCommTiers] = useState([]);

  // When package changes, load its supplier rates + commission rule + tiers
  useEffect(() => {
    if (!form.package_id) { setPackageRates([]); setSelectedPkg(null); setCommTiers([]); return; }
    supabase
      .from('package_supplier_rates')
      .select('*, package_suppliers!inner(package_id, suppliers(name, supplier_type)), currency:currency_id(code)')
      .eq('package_suppliers.package_id', form.package_id)
      .then(({ data }) => setPackageRates(data || []));
    supabase.from('packages').select('commission_percent, commission_basis, commission_type, package_name').eq('id', form.package_id).single()
      .then(({ data }) => setSelectedPkg(data));
    supabase.from('package_commission_tiers').select('*').eq('package_id', form.package_id).order('gp_from')
      .then(({ data }) => setCommTiers(data || []));
  }, [form.package_id]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Compute cost from package supplier rates per pax
  const numPax = parseInt(form.num_pax) || 1;
  const costPerPax = packageRates.reduce((sum, r) => {
    if (r.is_included) return sum;
    return sum + (parseFloat(r.cost_per_pax || 0) * parseFloat(r.budgeted_fx_rate || 1));
  }, 0);
  const computedCostLKR = costPerPax * numPax;

  const sellPerPax = parseFloat(form.sell_per_pax || 0);
  const totalSell = sellPerPax * numPax;
  const gpPerPax = sellPerPax - costPerPax;
  const gp = gpPerPax * numPax;
  const gpPct = sellPerPax > 0 ? ((gpPerPax / sellPerPax) * 100).toFixed(1) : 0;

  // Commission calc for preview
  const calcComm = () => {
    if (!selectedPkg || !form.care_of_agent_id) return 0;
    const commType = selectedPkg.commission_type || 'PERCENT';
    if (commType === 'TIERED') {
      const tier = commTiers.slice().sort((a, b) => parseFloat(b.gp_from) - parseFloat(a.gp_from))
        .find(t => gpPerPax >= parseFloat(t.gp_from) && (t.gp_to == null || gpPerPax < parseFloat(t.gp_to)));
      return tier ? parseFloat(tier.commission_amount) * numPax : 0;
    } else if (selectedPkg.commission_percent > 0) {
      const pct = parseFloat(selectedPkg.commission_percent);
      const basis = selectedPkg.commission_basis || 'ON_GP';
      const base = basis === 'ON_GP' ? gp : basis === 'ON_SELL' ? totalSell : computedCostLKR;
      return base * (pct / 100);
    }
    return 0;
  };
  const commAmount = calcComm();
  const netMargin = gp - commAmount;

  // Post the accounting journals for a confirmed booking: sale (DR AR / CR Revenue),
  // cost (DR Cost / CR Payable, per supplier type, offset against any prepaid advance),
  // and agent commission if any. total_cost is already stored in LKR; total_sell is in
  // the sell currency. Idempotent — skips if a BOOKING_COST entry already exists.
  const postBookingJournals = async (b, sellCode, supplierRows) => {
    const { data: existing } = await supabase.from('journal_entries').select('id')
      .eq('reference_type', 'BOOKING_COST').eq('reference_id', b.id).limit(1);
    if (existing && existing.length) return; // already posted

    const cost = parseFloat(b.total_cost || 0);

    if (cost > 0) {
      let lines = [];
      if (supplierRows && supplierRows.length > 0) {
        for (const bs of supplierRows) {
          const supplierType = bs.suppliers?.supplier_type || '';
          const amt = parseFloat(bs.cost_amount || 0);
          if (amt <= 0) continue;
          const payableAcc = payableAccountForSupplierType(supplierType);
          lines.push({ account_id: cogsAccountForSupplierType(supplierType, 'LKR'), debit_amount: amt, credit_amount: 0, description: `Cost - ${bs.suppliers?.name || 'Supplier'}` });
          lines.push({ account_id: payableAcc, debit_amount: 0, credit_amount: amt, description: `Payable - ${bs.suppliers?.name || 'Supplier'}`, party_type: 'supplier', party_id: bs.supplier_id });
          // Offset against any prepaid advance for this supplier on this package —
          // draws down Supplier Deposits instead of leaving fresh Payable on top of
          // money already paid.
          const remaining = await getSupplierDepositRemaining(b.package_id, bs.supplier_id);
          const offset = Math.min(amt, Math.max(0, remaining));
          if (offset > 0) {
            lines.push({ account_id: payableAcc, debit_amount: offset, credit_amount: 0, description: `Advance applied - ${bs.suppliers?.name || 'Supplier'}`, party_type: 'supplier', party_id: bs.supplier_id });
            lines.push({ account_id: ACCOUNTS.SUPPLIER_DEPOSITS, debit_amount: 0, credit_amount: offset, description: `Advance applied - ${bs.suppliers?.name || 'Supplier'}`, party_type: 'supplier', party_id: bs.supplier_id });
          }
        }
      } else {
        lines = [
          { account_id: ACCOUNTS.PACKAGE_COST_LKR, debit_amount: cost, credit_amount: 0, description: `Cost - ${b.booking_ref}` },
          { account_id: ACCOUNTS.HOTEL_LAND_PAYABLE, debit_amount: 0, credit_amount: cost, description: `Payable - ${b.booking_ref}` },
        ];
      }

      if (lines.length) {
        await postJournal({
          date: new Date().toISOString().slice(0, 10),
          description: `Booking cost: ${b.booking_ref}`,
          reference_type: 'BOOKING_COST',
          reference_id: b.id,
          exchange_rate: 1,
          lines,
          created_by: username,
          branch_id: selectedBranchId || null,
        });
      }
    }

    // Commission: recognise at confirmation (matches revenue recognition timing)
    const { data: comm } = await supabase.from('commissions')
      .select('agent_ref_id, commission_amount')
      .eq('reference_type', 'BOOKING').eq('reference_id', b.id)
      .maybeSingle();
    const commAmt = parseFloat(comm?.commission_amount || 0);
    if (commAmt > 0 && comm?.agent_ref_id) {
      const { data: existingComm } = await supabase.from('journal_entries').select('id')
        .eq('reference_type', 'BOOKING_COMMISSION').eq('reference_id', b.id).limit(1);
      if (!existingComm || !existingComm.length) {
        await postJournal({
          date: new Date().toISOString().slice(0, 10),
          description: `Agent commission: ${b.booking_ref}`,
          reference_type: 'BOOKING_COMMISSION',
          reference_id: b.id,
          exchange_rate: 1,
          lines: [
            { account_id: ACCOUNTS.COMMISSION_EXPENSE, debit_amount: commAmt, credit_amount: 0,      description: 'Commission expense' },
            { account_id: ACCOUNTS.COMMISSION_PAYABLE, debit_amount: 0,       credit_amount: commAmt, description: 'Commission payable', party_type: 'agent', party_id: comm.agent_ref_id },
          ],
          created_by: username,
          branch_id: selectedBranchId || null,
        });
      }
    }
  };

  const createBookingSuppliers = async (bookingId, packageId, numPax) => {
    if (!packageId) return;
    const { data: rates } = await supabase
      .from('package_supplier_rates')
      .select('cost_per_pax, budgeted_fx_rate, currency_id, is_included, package_suppliers!inner(supplier_id)')
      .eq('package_suppliers.package_id', packageId);
    const bySupplier = {};
    for (const r of rates || []) {
      if (r.is_included) continue;
      const sid = r.package_suppliers?.supplier_id;
      if (!sid) continue;
      if (!bySupplier[sid]) bySupplier[sid] = { costPerPax: 0, fxRate: parseFloat(r.budgeted_fx_rate || 1), currency_id: r.currency_id || null };
      bySupplier[sid].costPerPax += parseFloat(r.cost_per_pax || 0);
    }
    for (const [supplier_id, info] of Object.entries(bySupplier)) {
      const totalLkr = Math.round(info.costPerPax * info.fxRate * (parseInt(numPax) || 1) * 100) / 100;
      await supabase.from('booking_suppliers').upsert(
        { booking_id: bookingId, supplier_id, cost_amount: totalLkr, exchange_rate: info.fxRate, currency_id: info.currency_id, payment_status: 'UNPAID' },
        { onConflict: 'booking_id,supplier_id' }
      );
    }
  };

  const handleSubmit = async () => {
    if (!form.client_id || !form.num_pax) return setError('Client and pax count required');
    if (form.return_date && form.travel_date && form.return_date <= form.travel_date) return setError('Return date must be after travel date');
    setSaving(true); setError('');
    try {
      const { data: lastBK } = await supabase.from('bookings').select('booking_ref').ilike('booking_ref', 'BK%').order('booking_ref', { ascending: false }).limit(1);
      const lastSeq = lastBK?.[0]?.booking_ref ? parseInt(lastBK[0].booking_ref.replace('BK', '')) || 0 : 0;
      const booking_ref = 'BK' + String(lastSeq + 1).padStart(7, '0');
      const { data, error: err } = await supabase.from('bookings').insert({
        booking_ref,
        client_id: form.client_id,
        package_id: form.package_id || null,
        care_of_agent_id: form.care_of_agent_id || null,
        num_pax: numPax,
        booking_status: form.booking_status,
        payment_status: form.payment_status,
        total_cost: computedCostLKR || null,
        total_sell: totalSell || null,
        sell_currency_id: form.sell_currency_id || null,
        exchange_rate: parseFloat(form.exchange_rate) || 1,
        gross_profit: gp || null,
        gp_margin_percent: parseFloat(gpPct) || null,
        travel_date: form.travel_date || null,
        return_date: form.return_date || null,
        notes: form.notes,
        created_by: username,
        branch_id: selectedBranchId || null,
      }).select().single();
      if (err) throw err;

      // Auto-post commission
      if (form.package_id && form.care_of_agent_id && selectedPkg) {
        const commType = selectedPkg.commission_type || 'PERCENT';
        let commAmount = 0;
        let commPct = 0;
        const gpPerPax = gp / numPax;

        if (commType === 'TIERED') {
          const tier = commTiers.slice().sort((a, b) => parseFloat(b.gp_from) - parseFloat(a.gp_from))
            .find(t => gpPerPax >= parseFloat(t.gp_from) && (t.gp_to == null || gpPerPax < parseFloat(t.gp_to)));
          commAmount = tier ? parseFloat(tier.commission_amount) * numPax : 0;
        } else if (selectedPkg.commission_percent > 0) {
          commPct = parseFloat(selectedPkg.commission_percent);
          const basis = selectedPkg.commission_basis || 'ON_GP';
          const base = basis === 'ON_GP' ? gp : basis === 'ON_SELL' ? totalSell : computedCostLKR;
          commAmount = base * (commPct / 100);
        }

        if (commAmount > 0 || commType === 'TIERED') {
          await supabase.from('commissions').insert({
            booking_id: data.id,
            agent_ref_id: form.care_of_agent_id,
            product_type: 'Package',
            reference_type: 'BOOKING',
            reference_id: data.id,
            gp_amount: gp,
            gp_percent: parseFloat(gpPct),
            commission_percent: commPct,
            commission_basis: selectedPkg.commission_basis || null,
            commission_amount: commAmount,
            commission_status: 'PENDING',
          });
        }
      }

      // Revenue is recognised on confirmation, not while PENDING (a pending booking is
      // a quote). If the booking is created directly as CONFIRMED, post now.
      if (form.booking_status === 'CONFIRMED') {
        await createBookingSuppliers(data.id, data.package_id, data.num_pax);
        const { data: bsRows } = await supabase.from('booking_suppliers').select('*, suppliers(name, supplier_type)').eq('booking_id', data.id);
        await postBookingJournals(data, currencies.find(c => c.id === form.sell_currency_id)?.code, bsRows || []);
      }

      navigate(`/bookings/${data.id}`);
    } catch (err) { setError(err.message || 'Error saving'); setSaving(false); }
  };

  const handleCancel = async () => {
    if (!cancelReason) return;
    await supabase.from('bookings').update({ booking_status: 'CANCELLED', cancelled_by: username, cancelled_at: new Date().toISOString(), cancel_reason: cancelReason }).eq('id', id);
    const { data: jes } = await supabase.from('journal_entries').select('id')
      .in('reference_type', ['BOOKING', 'BOOKING_COST', 'BOOKING_COMMISSION'])
      .eq('reference_id', id).eq('status', 'POSTED').eq('is_reversed', false);
    for (const je of jes || []) await voidJournal(je.id, 'Booking cancelled: ' + cancelReason, username);
    // Any prepaid advance this booking had drawn down (via the offset lines inside its
    // BOOKING_COST journal, just reversed above) becomes available again for the next
    // booking against this package — the deposit pool is shared per package+supplier,
    // not per booking, so it is never forfeited here. Forfeiting the pool's remaining
    // balance only happens at package-level cancellation (Cancel Package).
    setCancelModal(false);
    supabase.from('bookings').select('*, clients(full_name), packages(package_name), sell_currency:sell_currency_id(code)').eq('id', id).single().then(({ data }) => setBooking(data));
  };

  const openEdit = () => {
    setEditForm({
      num_pax: String(booking.num_pax || 1),
      sell_per_pax: booking.total_sell && booking.num_pax ? String(parseFloat(booking.total_sell) / parseInt(booking.num_pax)) : '',
      sell_currency_id: booking.sell_currency_id || '',
      travel_date: booking.travel_date?.slice(0, 10) || '',
      return_date: booking.return_date?.slice(0, 10) || '',
      payment_status: booking.payment_status || 'UNPAID',
      notes: booking.notes || '',
    });
    setEditing(true);
  };

  const setEF = (k, v) => setEditForm(f => ({ ...f, [k]: v }));

  const handleSaveEdit = async () => {
    if (editForm.return_date && editForm.travel_date && editForm.return_date <= editForm.travel_date) {
      setError('Return date must be after travel date'); return;
    }
    setEditSaving(true);
    const pax = parseInt(editForm.num_pax) || 1;
    const spp = parseFloat(editForm.sell_per_pax) || 0;
    const totalSellNew = spp * pax;
    const gpNew = totalSellNew - parseFloat(booking.total_cost || 0);
    const gpPctNew = totalSellNew > 0 ? parseFloat(((gpNew / totalSellNew) * 100).toFixed(2)) : 0;
    await supabase.from('bookings').update({
      num_pax: pax,
      total_sell: totalSellNew,
      sell_currency_id: editForm.sell_currency_id || null,
      travel_date: editForm.travel_date || null,
      return_date: editForm.return_date || null,
      payment_status: editForm.payment_status,
      notes: editForm.notes,
      gross_profit: gpNew,
      gp_margin_percent: gpPctNew,
    }).eq('id', id);
    const { data } = await supabase.from('bookings').select('*, clients(full_name), packages(package_name), sell_currency:sell_currency_id(code), care_of_agent:care_of_agent_id(tls_code, full_name)').eq('id', id).single();
    setBooking(data);
    setEditing(false);
    setEditSaving(false);
  };

  if (isView) {
    if (!booking) return <div className="loading-screen"><div className="spinner" /></div>;
    return (
      <div>
        <div className="page-header">
          <div>
            <div className="page-title">{booking.booking_ref}</div>
            <div className="page-subtitle">{booking.clients?.full_name}  {booking.packages?.package_name}</div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {booking.booking_status !== 'CANCELLED' && (
              <button className="btn btn-secondary" onClick={openEdit}>Edit</button>
            )}
            <button className="btn btn-secondary" onClick={async () => {
              const { data: existing } = await supabase.from('invoices').select('id, invoice_type').eq('source_id', booking.id).neq('status', 'VOID').order('created_at', { ascending: false }).limit(5);
              const realInvoice = (existing || []).find(i => i.invoice_type === 'INVOICE');
              if (realInvoice) {
                window.location.href = `/invoices/${realInvoice.id}`;
                return;
              }
              // Auto-create invoice from booking data
              try {
                const { data: bsRows } = await supabase.from('booking_suppliers').select('*, suppliers(name, supplier_type)').eq('booking_id', booking.id).order('created_at');
                const lkrId = currencies.find(c => c.code === 'LKR')?.id;
                const yy = String(new Date().getFullYear()).slice(-2);
                const { data: maxRow } = await supabase.from('invoices').select('invoice_number').ilike('invoice_number', `TLS-${yy}-%`).order('invoice_number', { ascending: false }).limit(1);
                const lastSeq = maxRow?.[0]?.invoice_number ? parseInt(maxRow[0].invoice_number.split('-').pop()) : 0;
                const num = `TLS-${yy}-${String(lastSeq + 1).padStart(4, '0')}`;
                const numPax = booking.num_pax || 1;
                const totalAmount = parseFloat(booking.total_sell || 0);
                const amountPerPax = totalAmount / numPax;
                const lineItems = [{ description: booking.packages?.package_name || 'Travel Package', sub: null, qty: numPax, unitPrice: amountPerPax, amount: totalAmount }];
                // booking_suppliers.cost_amount is already total LKR (cost_per_pax × fxRate × numPax)
                // store per-pax in cost_items so GP display (which multiplies by pax) stays correct.
                // exchange_rate must be 1 here since cost_amount is already FX-converted — the real
                // fxRate was already applied when booking_suppliers.cost_amount was computed, so
                // re-applying it in the GP formula (cost_amount × exchange_rate) would double-convert.
                let costItems = (bsRows || []).filter(bs => bs.cost_amount).map(bs => ({
                  supplier_id: bs.supplier_id,
                  supplier_label: bs.suppliers?.name || '',
                  cost_amount: (parseFloat(bs.cost_amount) || 0) / numPax,
                  currency_id: bs.currency_id || lkrId,
                  exchange_rate: 1,
                  per_pax: true,
                }));
                // Generating the invoice straight from a published/pre-costed package, before
                // "Confirm" has ever been clicked (so booking_suppliers doesn't exist yet) —
                // pull costs directly from the package's own configured supplier rates instead
                // of leaving the invoice with zero cost. Same per-pax LKR / exchange_rate:1
                // contract as above, since these rates are already FX-converted here too.
                if (!costItems.length && booking.package_id) {
                  const { data: rates } = await supabase
                    .from('package_supplier_rates')
                    .select('cost_per_pax, budgeted_fx_rate, currency_id, is_included, package_suppliers!inner(supplier_id, suppliers(name))')
                    .eq('package_suppliers.package_id', booking.package_id);
                  const bySupplier = {};
                  for (const r of rates || []) {
                    if (r.is_included) continue;
                    const sid = r.package_suppliers?.supplier_id;
                    if (!sid) continue;
                    if (!bySupplier[sid]) bySupplier[sid] = { costPerPax: 0, fxRate: parseFloat(r.budgeted_fx_rate || 1), currency_id: r.currency_id || null, label: r.package_suppliers?.suppliers?.name || '' };
                    bySupplier[sid].costPerPax += parseFloat(r.cost_per_pax || 0);
                  }
                  costItems = Object.entries(bySupplier).map(([sid, info]) => ({
                    supplier_id: sid,
                    supplier_label: info.label,
                    cost_amount: info.costPerPax * info.fxRate,
                    currency_id: info.currency_id || lkrId,
                    exchange_rate: 1,
                    per_pax: true,
                  }));
                }
                const today = new Date().toISOString().slice(0, 10);
                // A published package's costs are already fully known from its own rate setup —
                // there's nothing left for an admin to manually cost, so skip Costing Pending.
                const { data: inv, error: invErr } = await supabase.from('invoices').insert({
                  invoice_number: num, invoice_type: 'INVOICE', client_id: booking.client_id,
                  source_type: 'booking', source_id: booking.id,
                  line_items: lineItems, cost_items: costItems,
                  costing_finalized: !!booking.package_id,
                  issued_date: today, status: 'DRAFT', created_by: username, branch_id: selectedBranchId || null,
                }).select().single();
                if (invErr) throw invErr;
                const sellCurr = currencies.find(c => c.id === booking.sell_currency_id);
                if (totalAmount > 0) {
                  const je = await postJournal({
                    date: today, description: `Invoice ${num}`, reference_type: 'INVOICE_BOOKING', reference_id: inv.id,
                    currency_id: booking.sell_currency_id || null, exchange_rate: rateFor(sellCurr?.code, 1),
                    lines: [
                      { account_id: ACCOUNTS.AR, debit_amount: totalAmount, credit_amount: 0, description: `AR - ${num}`, party_type: 'client', party_id: booking.client_id },
                      { account_id: ACCOUNTS.BOOKING_REVENUE, debit_amount: 0, credit_amount: totalAmount, description: `Revenue - ${num}` },
                    ], created_by: username, branch_id: selectedBranchId || null,
                  });
                  if (je?.id) await supabase.from('invoices').update({ journal_entry_id: je.id }).eq('id', inv.id);
                }
                // Costs already posted at booking confirmation (BOOKING_COST) — do not double-post
                window.location.href = `/invoices/${inv.id}`;
              } catch (e) { alert('Error creating invoice: ' + (e.message || e)); }
            }}>Generate Invoice</button>
            {booking.booking_status === 'PENDING' && (
              <button className="btn btn-primary" onClick={async () => {
                await createBookingSuppliers(id, booking.package_id, booking.num_pax);
                const { data: bsRows } = await supabase.from('booking_suppliers').select('*, suppliers(name, supplier_type)').eq('booking_id', id);
                await postBookingJournals(booking, booking.sell_currency?.code, bsRows || []);
                await supabase.from('bookings').update({ booking_status: 'CONFIRMED' }).eq('id', id);
                supabase.from('bookings').select('*, clients(full_name), packages(package_name), sell_currency:sell_currency_id(code), care_of_agent:care_of_agent_id(tls_code, full_name)').eq('id', id).single().then(({ data }) => setBooking(data));
                supabase.from('booking_suppliers').select('*, suppliers(name, supplier_type)').eq('booking_id', id).order('created_at').then(({ data }) => {
                  setBookingSuppliers(data || []);
                  const refs = {};
                  (data || []).forEach(r => { refs[r.id] = r.supplier_ref || ''; });
                  setSupplierRefEdit(refs);
                });
              }}>Confirm Booking</button>
            )}
            {booking.booking_status === 'CONFIRMED' && (
              <button className="btn btn-primary" onClick={async () => {
                await supabase.from('bookings').update({ booking_status: 'COMPLETED' }).eq('id', id);
                supabase.from('bookings').select('*, clients(full_name), packages(package_name), sell_currency:sell_currency_id(code), care_of_agent:care_of_agent_id(tls_code, full_name)').eq('id', id).single().then(({ data }) => setBooking(data));
              }}>Mark Completed</button>
            )}
            {booking.booking_status !== 'CANCELLED' && (
              <button className="btn btn-secondary" onClick={() => setCancelModal(true)}>Cancel Booking</button>
            )}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className="card">
            {[
              ['Client', booking.clients?.full_name],
              ['Package', booking.packages?.package_name],
              ['Agent', booking.agent?.full_name],
              ['Travel Date', booking.travel_date?.slice(0, 10)],
              ['Return Date', booking.return_date?.slice(0, 10)],
              ['Pax', booking.num_pax],
              ['Status', booking.booking_status],
              ['Payment', booking.payment_status],
            ].map(([l, v]) => v ? (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                <span style={{ color: 'var(--text-muted)' }}>{l}</span><span style={{ fontWeight: 500 }}>{v}</span>
              </div>
            ) : null)}
          </div>
          <div className="card">
            {(isAgent ? [
              ['Sell per Pax', booking.total_sell && booking.num_pax ? `${booking.sell_currency?.code || 'LKR'} ${(parseFloat(booking.total_sell) / parseInt(booking.num_pax)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''],
              ['Total Sell', `${booking.sell_currency?.code || ''} ${parseFloat(booking.total_sell || 0).toLocaleString()}`],
            ] : [
              ['Sell per Pax', booking.total_sell && booking.num_pax ? `${booking.sell_currency?.code || 'LKR'} ${(parseFloat(booking.total_sell) / parseInt(booking.num_pax)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''],
              ['Total Sell', `${booking.sell_currency?.code || ''} ${parseFloat(booking.total_sell || 0).toLocaleString()}`],
              ['Package Cost (LKR)', parseFloat(booking.total_cost || 0).toLocaleString()],
              ['Gross Profit', parseFloat(booking.gross_profit || 0).toLocaleString()],
              ['GP %', booking.gp_margin_percent ? booking.gp_margin_percent + '%' : ''],
            ]).map(([l, v]) => v ? (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                <span style={{ color: 'var(--text-muted)' }}>{l}</span><span style={{ fontWeight: 500 }}>{v}</span>
              </div>
            ) : null)}
            {(booking.created_by || booking.cancelled_by) && (
              <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)' }}>
                {booking.created_by && <div>Created by {booking.created_by}</div>}
                {booking.cancelled_by && <div style={{ color: 'var(--danger)', marginTop: 4 }}>Cancelled by {booking.cancelled_by}  {booking.cancel_reason}</div>}
              </div>
            )}
          </div>
        </div>
        {/* Supplier References panel — shown once booking_suppliers rows exist */}
        {bookingSuppliers.length > 0 && (
          <div className="card" style={{ marginTop: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>Supplier References</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)' }}>Supplier</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)' }}>Cost (LKR)</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)' }}>Paid</th>
                  <th style={{ textAlign: 'center', padding: '6px 10px', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)' }}>Status</th>
                  <th style={{ padding: '6px 10px', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)' }}>Your Ref / Serial No.</th>
                  <th style={{ width: 70 }}></th>
                </tr>
              </thead>
              <tbody>
                {bookingSuppliers.map(bs => (
                  <tr key={bs.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 500 }}>
                      {bs.suppliers?.name}
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{bs.suppliers?.supplier_type}</div>
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>{parseFloat(bs.cost_amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', color: '#16a34a' }}>{parseFloat(bs.amount_paid || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: bs.payment_status === 'PAID' ? '#16a34a' : bs.payment_status === 'PARTIAL' ? '#d97706' : '#dc2626' }}>
                        {bs.payment_status}
                      </span>
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <input className="form-input" style={{ fontSize: 13, padding: '4px 8px' }}
                        value={supplierRefEdit[bs.id] ?? ''} placeholder="e.g. my-27-16"
                        onChange={e => setSupplierRefEdit(r => ({ ...r, [bs.id]: e.target.value }))} />
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <button className="btn btn-sm btn-primary" disabled={savingRef[bs.id]}
                        onClick={async () => {
                          setSavingRef(s => ({ ...s, [bs.id]: true }));
                          await supabase.from('booking_suppliers').update({ supplier_ref: supplierRefEdit[bs.id] || null }).eq('id', bs.id);
                          setBookingSuppliers(prev => prev.map(r => r.id === bs.id ? { ...r, supplier_ref: supplierRefEdit[bs.id] } : r));
                          setSavingRef(s => ({ ...s, [bs.id]: false }));
                        }}>
                        {savingRef[bs.id] ? '…' : 'Save'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {booking.passengers && booking.passengers.length > 0 && (
          <div className="card" style={{ marginTop: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>Passengers</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)', width: 36 }}>#</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)' }}>Name</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, fontSize: 12, color: 'var(--text-muted)' }}>Ticket Number</th>
                </tr>
              </thead>
              <tbody>
                {booking.passengers.map((p, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '7px 10px', color: 'var(--text-muted)', fontSize: 12 }}>{i + 1}</td>
                    <td style={{ padding: '7px 10px', fontWeight: 500 }}>{p.name || '—'}</td>
                    <td style={{ padding: '7px 10px', fontFamily: 'monospace', fontSize: 12 }}>{p.ticket_number || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {editing && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div className="card" style={{ width: 520, padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
              <div className="card-title" style={{ marginBottom: 16 }}>Edit Booking — {booking.booking_ref}</div>
              <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="form-group">
                  <label className="form-label">No. of Pax</label>
                  <input className="form-input" type="text" inputMode="numeric" value={editForm.num_pax} onChange={e => setEF('num_pax', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Sell per Pax</label>
                  <input className="form-input" type="text" inputMode="decimal" value={editForm.sell_per_pax} onChange={e => setEF('sell_per_pax', e.target.value)} placeholder="0.00" />
                </div>
                <div className="form-group">
                  <label className="form-label">Sell Currency</label>
                  <TypeSearch options={currencies} value={currencies.find(c => c.id === editForm.sell_currency_id)?.code || ''} displayKey="code" placeholder="Currency…" onSelect={c => setEF('sell_currency_id', c.id)} onClear={() => setEF('sell_currency_id', '')} />
                </div>
                <div className="form-group">
                  <label className="form-label">Payment Status</label>
                  <TypeSearch options={['UNPAID','PARTIAL','PAID'].map(s => ({ id: s, label: s }))} value={editForm.payment_status} displayKey="label" placeholder="Status…" onSelect={s => setEF('payment_status', s.id)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Travel Date</label>
                  <input className="form-input" type="date" value={editForm.travel_date} onChange={e => setEF('travel_date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Return Date</label>
                  <input className="form-input" type="date" value={editForm.return_date} onChange={e => setEF('return_date', e.target.value)} />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Notes / Inclusions</label>
                  <textarea className="form-input" rows={4} value={editForm.notes} onChange={e => setEF('notes', e.target.value)} placeholder="Package inclusions, special notes…" style={{ resize: 'vertical' }} />
                </div>
              </div>
              {editForm.sell_per_pax && editForm.num_pax && (
                <div style={{ background: 'var(--bg)', borderRadius: 7, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: 'var(--text-muted)' }}>Total Sell</span>
                    <span style={{ fontWeight: 600 }}>{(parseFloat(editForm.sell_per_pax) * parseInt(editForm.num_pax)).toLocaleString()}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: 'var(--text-muted)' }}>Gross Profit</span>
                    <span style={{ fontWeight: 600 }}>{((parseFloat(editForm.sell_per_pax) * parseInt(editForm.num_pax)) - parseFloat(booking.total_cost || 0)).toLocaleString()}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-muted)' }}>GP %</span>
                    <span style={{ fontWeight: 600, color: '#16a34a' }}>
                      {(() => { const ts = parseFloat(editForm.sell_per_pax) * parseInt(editForm.num_pax); const gp = ts - parseFloat(booking.total_cost || 0); return ts > 0 ? ((gp / ts) * 100).toFixed(1) + '%' : '—'; })()}
                    </span>
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setEditing(false)} disabled={editSaving}>Cancel</button>
                <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleSaveEdit} disabled={editSaving}>{editSaving ? 'Saving…' : 'Save Changes'}</button>
              </div>
            </div>
          </div>
        )}

        {cancelModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div className="card" style={{ width: 400, padding: 24 }}>
              <div className="card-title" style={{ marginBottom: 12 }}>Cancel Booking</div>
              <div className="form-group" style={{ marginBottom: 16 }}><label className="form-label">Cancel Reason *</label><input className="form-input" value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="Reason..." /></div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setCancelModal(false)}>Back</button>
                <button className="btn" style={{ flex: 1, background: 'var(--danger)', color: '#fff' }} onClick={handleCancel} disabled={!cancelReason}>Confirm Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">New Booking</div></div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div>
            <label className="form-label">Branch</label>
            <select className="form-input" style={{ minWidth: 140 }} value={selectedBranchId} onChange={e => setSelectedBranchId(e.target.value)}>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-secondary" onClick={() => navigate(-1)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving...' : 'Create Booking'}</button>
          </div>
        </div>
      </div>
      {error && <div style={{ color: 'var(--danger)', marginBottom: 14, padding: '10px 14px', background: 'rgba(239,68,68,0.06)', borderRadius: 7 }}>{error}</div>}
      <div className="card">
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Client *</label>
            <TypeSearch
              fetchFn={async (q) => { const { data } = await supabase.from('clients').select('id, full_name, client_code').eq('is_active', true).or(`full_name.ilike.%${q}%,client_code.ilike.%${q}%`).order('full_name').limit(30); return (data || []).map(c => ({ ...c, label: `${c.full_name} (${c.client_code})` })); }}
              value={clientLabel}
              displayKey="label"
              placeholder="Type client name…"
              onSelect={c => { setClientLabel(`${c.full_name} (${c.client_code})`); set('client_id', c.id); }}
              onClear={() => { setClientLabel(''); set('client_id', ''); }}
            />
          </div>
          {!isAgent && (
            <div className="form-group">
              <label className="form-label">Care of (Agent)</label>
              <TypeSearch options={agents} value={form.care_of_agent_label} displayKey="label" placeholder="TLS code or agent name…" onSelect={a => { set('care_of_agent_id', a.id); set('care_of_agent_label', a.label); }} onClear={() => { set('care_of_agent_id', ''); set('care_of_agent_label', ''); }} renderOption={a => <><strong style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--primary)' }}>{a.tls_code}</strong><span style={{ marginLeft: 10, fontSize: 13 }}>{a.full_name}</span></>} />
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Package</label>
            <TypeSearch options={packages.map(p => ({ ...p, label: p.package_name }))} value={packages.find(p => p.id === form.package_id)?.package_name || ''} displayKey="label" placeholder="Type package name…" onSelect={p => set('package_id', p.id)} onClear={() => set('package_id', '')} />
          </div>
          <div className="form-group">
            <label className="form-label">Agent (for commission)</label>
            <TypeSearch options={agents} value={agents.find(a => a.id === form.agent_id)?.label || ''} displayKey="label" placeholder="TLS code or agent name…" onSelect={a => set('agent_id', a.id)} onClear={() => set('agent_id', '')} renderOption={a => <><strong style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--primary)' }}>{a.tls_code}</strong><span style={{ marginLeft: 10, fontSize: 13 }}>{a.full_name}</span></>} />
          </div>
          <div className="form-group"><label className="form-label">No. of Pax *</label><input className="form-input" type="text" inputMode="numeric" value={form.num_pax} onChange={e => set('num_pax', e.target.value)} /></div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <TypeSearch options={['PENDING','CONFIRMED','COMPLETED','CANCELLED'].map(s => ({ id: s, label: s }))} value={form.booking_status} displayKey="label" placeholder="Status…" onSelect={s => set('booking_status', s.id)} />
          </div>
          <div className="form-group"><label className="form-label">Travel Date</label><input className="form-input" type="date" value={form.travel_date} onChange={e => set('travel_date', e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Return Date</label><input className="form-input" type="date" value={form.return_date} onChange={e => set('return_date', e.target.value)} /></div>

          {/* Package cost — read only, computed from supplier rates */}
          {form.package_id && (
            <div className="form-group">
              <label className="form-label">Package Cost (LKR) — from supplier rates</label>
              <div style={{ padding: '10px 12px', borderRadius: 7, background: 'var(--bg)', border: '1px solid var(--border)', fontSize: 14 }}>
                {packageRates.length === 0
                  ? <span style={{ color: 'var(--text-muted)' }}>No rates set on this package</span>
                  : (
                    <>
                      <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>LKR {costPerPax.toLocaleString()} <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--text-muted)' }}>per pax</span></div>
                      {packageRates.map(r => (
                        <div key={r.id} style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between' }}>
                          <span>{r.package_suppliers?.suppliers?.name}</span>
                          <span>{r.currency?.code} {parseFloat(r.cost_per_pax || 0).toLocaleString()} × {r.budgeted_fx_rate || 1} = LKR {(parseFloat(r.cost_per_pax || 0) * parseFloat(r.budgeted_fx_rate || 1)).toLocaleString()}</span>
                        </div>
                      ))}
                    </>
                  )
                }
              </div>
            </div>
          )}

          <div className="form-group"><label className="form-label">Sell Price per Pax (LKR)</label><input className="form-input" type="text" inputMode="decimal" value={form.sell_per_pax} onChange={e => set('sell_per_pax', e.target.value)} placeholder="0.00" /></div>
          <div className="form-group">
            <label className="form-label">Sell Currency</label>
            <TypeSearch options={currencies} value={currencies.find(c => c.id === form.sell_currency_id)?.code || ''} displayKey="code" placeholder="Currency…" onSelect={c => set('sell_currency_id', c.id)} onClear={() => set('sell_currency_id', '')} />
          </div>

          {sellPerPax > 0 && form.package_id && (
            <div className="form-group full-width">
              <label className="form-label">Margin Breakdown</label>
              <div style={{ borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden', fontSize: 13 }}>
                {/* Header row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', background: 'var(--bg)', padding: '8px 14px', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', borderBottom: '1px solid var(--border)' }}>
                  <span></span><span style={{ textAlign: 'right' }}>Per Pax</span><span style={{ textAlign: 'right' }}>Total ({numPax} pax)</span>
                </div>
                {[
                  ['Sell Price', sellPerPax, totalSell, false],
                  ['Package Cost', costPerPax, computedCostLKR, false],
                  ['Gross Profit', gpPerPax, gp, true],
                  ...(commAmount > 0 ? [['Agent Commission', commAmount / numPax, commAmount, false, true]] : []),
                  ...(commAmount > 0 ? [['Net Margin', gpPerPax - commAmount / numPax, netMargin, true]] : []),
                ].map(([label, perPax, total, bold, isComm]) => (
                  <div key={label} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '9px 14px', borderBottom: '1px solid var(--border)', background: bold && !isComm ? 'rgba(79,110,247,0.04)' : 'white', fontWeight: bold ? 700 : 400 }}>
                    <span style={{ color: isComm ? 'var(--text-muted)' : 'inherit' }}>{label}</span>
                    <span style={{ textAlign: 'right', color: bold ? (perPax >= 0 ? 'var(--success)' : 'var(--danger)') : isComm ? 'var(--warning, #d97706)' : 'inherit' }}>
                      {perPax.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                    <span style={{ textAlign: 'right', color: bold ? (total >= 0 ? 'var(--success)' : 'var(--danger)') : isComm ? 'var(--warning, #d97706)' : 'inherit' }}>
                      {total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                  </div>
                ))}
                <div style={{ padding: '8px 14px', fontSize: 12, color: 'var(--text-muted)', background: 'var(--bg)' }}>
                  GP%: {gpPct}% per pax
                </div>
              </div>
            </div>
          )}

          <div className="form-group full-width"><label className="form-label">Notes</label><textarea className="form-textarea" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
        </div>
      </div>
    </div>
  );
}
